import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { API_BASE_URL } from '../../config';
import AdminSidebar from './AdminSidebar';
import './AdminDashboard.css';

const AdminDashboard = () => {
    const { t } = useTranslation(['admin', 'common']);
    const [stats, setStats] = useState(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('overview');
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const navigate = useNavigate();
    const adminUsername = localStorage.getItem('adminUsername');

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}get_admin_stats.php`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('adminId')}`
                },
                credentials: 'include'
            });
            const data = await response.json();
            if (data.status === 'success') {
                setStats(data.stats);
            } else {
                setError(data.message);
                if (data.message === 'Unauthorized access.') {
                    handleLogout();
                }
            }
        } catch (err) {
            setError(t('admin:dashboard.errors.loadFailed', 'Failed to load dashboard data'));
        } finally {

            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('adminId');
        localStorage.removeItem('adminUsername');
        navigate('/admin/login');
    };

    if (loading) return (
        <div className="admin-loading-screen">
            <div className="loader-inner">
                <div className="loader-orbit"></div>
                <div className="loader-text">{t('admin:dashboard.loading', 'Initializing Admin Terminal...')}</div>
            </div>
        </div>

    );

    return (
        <div className={`admin-layout${sidebarOpen ? '' : ' sidebar-collapsed'}`}>
            <AdminSidebar sidebarOpen={sidebarOpen} onToggle={() => setSidebarOpen(o => !o)} />

            {/* Main Content Area */}
            <main className="admin-main">
                <header className="main-header">
                    {/* Collapse toggle — placed before all header content */}
                    <button
                        className="sidebar-toggle-btn"
                        onClick={() => setSidebarOpen(o => !o)}
                        aria-label="Toggle sidebar"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '42px',
                            height: '42px',
                            fontSize: '1.4rem',
                            color: '#f8fafc',
                            background: 'rgba(59,130,246,0.15)',
                            border: '1px solid rgba(59,130,246,0.4)',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            flexShrink: 0,
                            padding: 0,
                        }}
                    >
                        ☰
                    </button>

                    <div className="header-search">
                        <input type="text" placeholder={t('admin:dashboard.searchPlaceholder', 'Search users, tests, reports...')} />
                        <span className="search-icon">🔍</span>
                    </div>

                    <div className="header-actions">
                        <button className="noti-btn">🔔<span className="badge">3</span></button>
                        <div className="date-display">{new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                    </div>
                </header>


                <section className="content-inner">
                    <div className="welcome-banner">
                        <div className="banner-content">
                            <h1>{t('admin:dashboard.title', 'Terminal Control Center')}</h1>
                            <p>{t('admin:dashboard.welcome', { count: stats?.recentUsers?.length || 0, defaultValue: `Everything is running smoothly. You have ${stats?.recentUsers?.length || 0} new registrations today.` })}</p>
                        </div>

                        <div className="banner-visual">
                            <div className="blob"></div>
                        </div>
                    </div>

                    {error && <div className="error-alert">{error}</div>}

                    {/* Stats Grid */}
                    <div className="metrics-grid">
                        <div className="metric-card">
                            <div className="metric-header">
                                <span className="label">{t('admin:dashboard.metrics.totalReach', 'Total Reach')}</span>
                                <span className="trend positive">+12%</span>
                            </div>
                            <div className="metric-body">
                                <h2 className="value">{stats?.totalUsers || 0}</h2>
                                <p className="subtext">{t('admin:dashboard.metrics.activeLearners', 'Active Learners')}</p>
                            </div>

                            <div className="metric-footer">
                                <div className="progress-mini"><div className="fill" style={{ width: '75%' }}></div></div>
                            </div>
                        </div>

                        <div className="metric-card">
                            <div className="metric-header">
                                <span className="label">{t('admin:dashboard.metrics.assessments', 'Assessments')}</span>
                                <span className="trend positive">+5%</span>
                            </div>
                            <div className="metric-body">
                                <h2 className="value">{stats?.totalTests || 0}</h2>
                                <p className="subtext">{t('admin:dashboard.metrics.completedTests', 'Completed Tests')}</p>
                            </div>

                            <div className="metric-footer">
                                <div className="progress-mini"><div className="fill" style={{ width: '45%' }}></div></div>
                            </div>
                        </div>

                        <div className="metric-card">
                            <div className="metric-header">
                                <span className="label">{t('admin:dashboard.metrics.growth', 'Growth')}</span>
                                <span className="trend positive">+24%</span>
                            </div>
                            <div className="metric-body">
                                <h2 className="value">{t('admin:dashboard.metrics.new', 'New')}</h2>
                                <p className="subtext">{t('admin:dashboard.metrics.marketAdoption', 'Market Adoption')}</p>
                            </div>

                            <div className="metric-footer">
                                <div className="progress-mini"><div className="fill" style={{ width: '90%' }}></div></div>
                            </div>
                        </div>
                    </div>

                    {/* Data Tables / Content Sections */}
                    <div className="dashboard-grid">
                        <div className="data-panel large">
                            <div className="panel-header">
                                <h2>{t('admin:dashboard.recentRegistrations.title', 'Recent Registrations')}</h2>
                                <button className="view-all" onClick={() => navigate('/admin/users')}>{t('admin:dashboard.recentRegistrations.viewAll', 'View All')}</button>
                            </div>

                            <div className="table-wrapper">
                                <table className="premium-table">
                                    <thead>
                                        <tr>
                                            <th>{t('admin:dashboard.recentRegistrations.student', 'Student')}</th>
                                            <th>{t('admin:dashboard.recentRegistrations.email', 'Email Address')}</th>
                                            <th>{t('admin:dashboard.recentRegistrations.joinedDate', 'Joined Date')}</th>
                                            <th>{t('admin:dashboard.recentRegistrations.status', 'Status')}</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {stats?.recentUsers?.map((user, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <div className="user-cell">
                                                        <div className="user-initials">{user.full_name.charAt(0)}</div>
                                                        <span>{user.full_name}</span>
                                                    </div>
                                                </td>
                                                <td>{user.email}</td>
                                                <td>{new Date(user.created_at).toLocaleDateString()}</td>
                                                <td><span className="status-pill active">{t('admin:dashboard.recentRegistrations.active', 'Active')}</span></td>
                                            </tr>

                                        ))}
                                        {(!stats?.recentUsers || stats.recentUsers.length === 0) && (
                                            <tr>
                                                <td colSpan="4" className="empty-state">{t('admin:dashboard.recentRegistrations.empty', 'No recent activity detected')}</td>
                                            </tr>
                                        )}

                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="data-panel small">
                            <div className="panel-header">
                                <h2>{t('admin:dashboard.quickActions.title', 'Quick Actions')}</h2>
                            </div>

                            <div className="action-list">
                                <button className="action-item">
                                    <span className="action-icon">➕</span>
                                    <div className="action-text">
                                        <p className="title">{t('admin:dashboard.quickActions.addAdmin.title', 'Add New Admin')}</p>
                                        <p className="desc">{t('admin:dashboard.quickActions.addAdmin.desc', 'Grant portal access')}</p>
                                    </div>
                                </button>

                                <button className="action-item">
                                    <span className="action-icon">📁</span>
                                    <div className="action-text">
                                        <p className="title">{t('admin:dashboard.quickActions.exportData.title', 'Export Data')}</p>
                                        <p className="desc">{t('admin:dashboard.quickActions.exportData.desc', 'Generate CSV report')}</p>
                                    </div>
                                </button>

                                <button className="action-item">
                                    <span className="action-icon">💬</span>
                                    <div className="action-text">
                                        <p className="title">{t('admin:dashboard.quickActions.supportTickets.title', 'Support Tickets')}</p>
                                        <p className="desc">{t('admin:dashboard.quickActions.supportTickets.desc', 'View user inquiries')}</p>
                                    </div>
                                </button>

                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
};

export default AdminDashboard;
